import cn from 'classnames';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

type HorizontalScrollerProps = {
    children: ReactNode;
    scrollAmount?: number;
    className?: string;
};
export function HorizontalScroller({ children, scrollAmount = 300, className = '' }: HorizontalScrollerProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanScrollLeft(scrollLeft > 0);
        // small buffer for sub-pixel rounding
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1 - 24);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener('scroll', updateScrollState, {
            passive: true
        });
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(el);
        Array.from(el.children).forEach((child) => resizeObserver.observe(child));
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            resizeObserver.disconnect();
        };
    }, [updateScrollState, children]);

    const scrollBy = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        if (direction === 'left') el.scrollTo({ left: 0, behavior: 'smooth' });
        else el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
    };
    return (
        <div className={`relative w-full ${className}`}>
            {canScrollLeft && (
                <button
                    type="button"
                    aria-label="Scroll left"
                    onClick={() => scrollBy('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex bg-brand-background items-center justify-center text-brand-text hover:text-brand-primary-light transition focus:outline-none">
                    <LuChevronLeft className="h-3.5 w-3.5" />
                </button>
            )}

            <div
                ref={scrollRef}
                className={cn(
                    'flex gap-4 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory transition-all duration-150',
                    {
                        'mr-6': canScrollRight,
                        'ml-6': canScrollLeft
                    }
                )}>
                {children}
            </div>

            {canScrollRight && (
                <button
                    type="button"
                    aria-label="Scroll right"
                    onClick={() => scrollBy('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex h-5 items-center justify-center rounded-sm text-brand-text hover:text-brand-primary-light transition focus:outline-none">
                    <LuChevronRight className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}
